import CheckCircle from '@mui/icons-material/CheckCircle'
import Language from '@mui/icons-material/Language'
import {Avatar, Box, ListItemIcon, ListItemText, Menu, MenuItem, Tooltip} from '@mui/material'
import flagFr from 'flag-icons/flags/4x3/fr.svg'
import flagEn from 'flag-icons/flags/4x3/us.svg'
import {bindMenu} from 'material-ui-popup-state'
import {bindTrigger, usePopupState} from 'material-ui-popup-state/hooks'
import {memo} from 'react'
import {useTranslation} from 'react-i18next'
import type {Locale, LocaleObject} from '../../../@types/Locale'
import {AnimatedIconButton} from '../../../components/AnimatedIconButton'
import {locales} from '../../../enums/locale'
import {useUserHelperGetCurrent, useUserUpdate} from '../../../hooks/entity/useUser'
import {useEffectEvent} from '../../../hooks/useEffectEvent'
import {useErrorHandler} from '../../../hooks/useErrorHandler'

const getFlag = (locale: Locale) => {
  if (locale === 'fr') {
    return flagFr
  }

  return flagEn
}

interface LocaleSelectorItemProps {
  locale: LocaleObject
  onSelect: (locale: string) => void
}

const LocaleSelectorItem = memo(({locale, onSelect}: LocaleSelectorItemProps) => {
  const {i18n} = useTranslation('translation')

  const handleClick = useEffectEvent(() => onSelect(locale.code))

  return <MenuItem key={locale.code} onClick={handleClick}>
    <ListItemIcon>
      <Avatar variant="rounded" src={getFlag(locale.code)} sx={{width: 30, height: 20}}/>
      {locale.code === i18n.resolvedLanguage && (
        <Box sx={{position: 'absolute', top: 0, left: 4}}>
          <CheckCircle color="success" sx={{fontSize: 16}}/>
        </Box>
      )}
    </ListItemIcon>
    <ListItemText primary={locale.label}/>
  </MenuItem>
})

export const LocaleSelector = memo(() => {
  const popupState = usePopupState({variant: 'popper'})
  const {i18n} = useTranslation('translation')
  const {t} = useTranslation()
  const {user} = useUserHelperGetCurrent()
  const userUpdate = useUserUpdate()
  const errorHandler = useErrorHandler()

  const handleSelect = useEffectEvent(async (locale: string) => {
    popupState.close()
    if (!locale) {
      return
    }
    await i18n.changeLanguage(locale)
    if (user) {
      try {
        await userUpdate(user, {
          locale,
        })
      } catch (err: any) {
        errorHandler(err)
      }
    }
  })

  if (!i18n.resolvedLanguage) {
    return null
  }

  const localeData = locales.find((l: LocaleObject) => l.code === i18n.resolvedLanguage)
  if (!localeData) {
    return null
  }

  return <>
    <Tooltip title={t('common.menu.locale')}>
      <AnimatedIconButton
        size="large"
        {...bindTrigger(popupState)}
      >
        <Language/>
      </AnimatedIconButton>
    </Tooltip>
    <Menu
      {...bindMenu(popupState)}
      anchorOrigin={{vertical: 'bottom', horizontal: 'right'}}
      transformOrigin={{vertical: 'bottom', horizontal: 'left'}}
    >
      {locales.map((l: LocaleObject) => <LocaleSelectorItem key={l.code} locale={l} onSelect={handleSelect}/>)}
    </Menu>
  </>
})
